import { newsArticles, categories, tagOptions, hotKeywords } from '@/mocks/news';
import type { NewsArticle, NewsCategory } from '@/types/news';

export const allArticles = newsArticles as unknown as NewsArticle[];
export const allCategories = categories as NewsCategory[];
export const allTags: string[] = tagOptions;
export const popularKeywords: string[] = hotKeywords;

export function getCategory(key?: string): NewsCategory | undefined {
  return allCategories.find((c) => c.key === key);
}

export function getCategoryName(key?: string): string {
  return getCategory(key)?.name ?? '资讯';
}

export function getArticleById(id?: string): NewsArticle | undefined {
  return allArticles.find((a) => a.id === id);
}

export function getArticlesByCategory(key?: string): NewsArticle[] {
  return allArticles.filter((a) => a.category === key);
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  const hh = `${d.getHours()}`.padStart(2, '0');
  const mm = `${d.getMinutes()}`.padStart(2, '0');
  return `${y}-${m}-${day} ${hh}:${mm}`;
}

export function formatDateShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${m}-${day}`;
}

export function formatReadCount(count: number): string {
  if (count >= 10000) return `${(count / 10000).toFixed(1)}万`;
  return `${count}`;
}

export interface ArticleQuery {
  category?: string;
  tag?: string;
  keyword?: string;
  sort?: 'latest' | 'hot';
}

export function queryArticles({ category, tag, keyword, sort = 'latest' }: ArticleQuery): NewsArticle[] {
  let list = [...allArticles];
  if (category) list = list.filter((a) => a.category === category);
  if (tag) list = list.filter((a) => a.tags.includes(tag));
  if (keyword && keyword.trim()) {
    const kw = keyword.trim().toLowerCase();
    list = list.filter(
      (a) =>
        a.title.toLowerCase().includes(kw) ||
        a.summary.toLowerCase().includes(kw) ||
        a.tags.some((t) => t.toLowerCase().includes(kw)),
    );
  }
  list.sort((a, b) => {
    if (sort === 'hot') return b.readCount - a.readCount;
    return new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime();
  });
  return list;
}

export function getRelatedArticles(article: NewsArticle, limit = 5): NewsArticle[] {
  return allArticles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .sort((a, b) => b.readCount - a.readCount)
    .slice(0, limit);
}

export function getLatestArticles(limit = 6): NewsArticle[] {
  return [...allArticles]
    .sort((a, b) => new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime())
    .slice(0, limit);
}

export function getHotArticles(limit = 8): NewsArticle[] {
  return [...allArticles].sort((a, b) => b.readCount - a.readCount).slice(0, limit);
}

export function getFeaturedArticles(limit = 4): NewsArticle[] {
  return [...allArticles]
    .sort((a, b) => b.readCount * 1 + new Date(b.publishTime).getTime() / 1e10 - (a.readCount * 1 + new Date(a.publishTime).getTime() / 1e10))
    .slice(0, limit);
}